import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ModalService } from '@app/core/services/modal.service';
import { TranslateService } from '@app/core/services/translate.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { OeeDataGet, OeeSelectionTarget, OeeService } from '@app/services/oee.service';

@Component({
  selector: 'tt-oee-data',
  templateUrl: './oee-data.component.html',
  styleUrls: ['./oee-data.component.css'],
  standalone: false,
})
export class OeeDataComponent implements OnInit, OnDestroy {
    public page?: OeeDataGet;
    public oeeStates: Item[] = [];
    public registeredLabours: Item[] = [];
    public oeeDataKeyno = '';
    public showRemarkText = '';
    public saving = false;
    public approving = false;
    public deleting = false;
    public error = '';
    private settingsRevision = 0;
    private destroyed = false;

    constructor(private oee: OeeService, private modal: ModalService, private translate: TranslateService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator) {
        this.navigator = useNavigatorFor(navigator, this);
    }
    public get busy(): boolean { return this.saving || this.approving || this.deleting; }
    public get canDelete(): boolean { return Number(this.page?.oeedata_keyno) > 0; }

    public async ngOnInit(): Promise<void> {
        this.oeeDataKeyno = String(this.navigator?.params['oeedata_keyno'] ?? '');
        try {
            this.showRemarkText = this.oee.showRemarkText;
            const loadPage = async () => {
                const page = await this.oee.getEditorData(this.oeeDataKeyno);
                if (this.destroyed) return;
                this.page = page;
                if (page?.equipment_id !== undefined && String(page.equipment_id).trim()) this.oeeStates = await this.oee.getStates(page.equipment_id);
            };
            await Promise.all([loadPage(), this.oee.getRegisteredLabours().then(items => this.registeredLabours = items)]);
        } catch (error) { this.showError(error); }
    }

    public goToSelection(target: OeeSelectionTarget, event?: { ctrlKey?: boolean }): void {
        if (!this.page) return;
        this.oee.prepareSelection(this.page, target);
        const parms = target === 'selectoeestate' ? { arrowside: 'L' } : target === 'selectlabourpu' ? { webpagename: 'oeedata' } : { oeestate_keyno: this.page.oeestate_keyno };
        if (event?.ctrlKey) this.navigator?.newTab({ to: `${target}-test`, parms });
        else this.navigator?.go({ to: `${target}-test`, parms });
    }

    public async onOeeStateChanged(value: string | number | undefined): Promise<void> {
        if (!this.page || value === undefined || this.page.oeestate_keyno_new === value) return;
        this.page.oeestate_keyno_new = value;
        const revision = ++this.settingsRevision;
        if (!String(value).trim()) return;
        this.page.oeestate_keyno = value;
        this.page.oeestate_name = String(value);
        try {
            const settings = await this.oee.getShowSettings(value);
            if (revision === this.settingsRevision && !this.destroyed) {
                this.page.show_details = settings.show_details;
                this.page.show_remark = settings.show_remark;
                this.page.show_prodid = settings.show_prodid;
            }
        } catch (error) { this.showError(error); }
    }

    public async saveChanges(): Promise<void> {
        if (!this.page || this.busy) return;
        this.saving = true;
        this.error = '';
        try {
            const response = await this.oee.saveData(this.page);
            if (response?.saveok === '1') {
                const words = await this.translate.translateBatch({ confirmation: '', ok: '' });
                await this.modal.show({ type: 'success', title: words.confirmation, message: response.promptmessage,
                    buttons: [{ label: words.ok, cssClass: 'btn-success', close: true }] });
                await this.navigator?.back();
            }
        } catch (error) { this.showError(error); }
        finally { this.saving = false; }
    }

    public async approveChanges(): Promise<void> {
        if (!this.page || this.busy) return;
        this.approving = true;
        this.error = '';
        try { await this.oee.approveData(this.page); await this.navigator?.back(); }
        catch (error) { this.showError(error); }
        finally { this.approving = false; }
    }

    public async deleteValues(): Promise<void> {
        if (!this.page || this.busy) return;
        this.deleting = true;
        this.error = '';
        try {
            const words = await this.translate.translateBatch({ confirm_title: '', confirm_message: '', confirm_ok_label: '', confirm_cancel_label: '' });
            if (!await this.modal.openConfirmDialog({ type: 'danger', title: words.confirm_title, message: words.confirm_message, ok: words.confirm_ok_label, cancel: words.confirm_cancel_label })) return;
            await this.oee.deleteData(this.page.oeedata_keyno);
            await this.navigator?.back();
        } catch (error) { this.showError(error); }
        finally { this.deleting = false; }
    }

    private showError(error: unknown): void { this.error = error instanceof Error ? error.message : String(error); }
    public ngOnDestroy(): void { this.destroyed = true; ++this.settingsRevision; }

}
